(() => {
  class InputSelect extends HTMLElement {
    /** @type {boolean} */
    _initialized = false;
    /** @type {HTMLSelectElement|null} */
    select = null;
    /** @type {HTMLButtonElement|null} */
    trigger = null;
    /** @type {HTMLElement|null} */
    menu = null;
    /** @type {MutationObserver|null} */
    _observer = null;
    /** @type {MutationObserver|null} */
    _attrObserver = null;
    /** @type {(e: MouseEvent) => void} */
    boundOutside = () => {};
    /** @type {() => boolean} */
    isDisabled = () => false;

    connectedCallback() {
      console.log('InputSelect connected');
      if (this._initialized) return;
      this._initialized = true;
      this.select = /** @type {HTMLSelectElement|null} */ (this.querySelector('select'));
      this.trigger = /** @type {HTMLButtonElement|null} */ (this.querySelector('.is-trigger'));
      this.menu = /** @type {HTMLElement|null} */ (this.querySelector('.is-menu'));
      if (!this.select || !this.trigger || !this.menu) return;

      this.isDisabled = () => {
        // Source of truth is the native select.
        return !!(this.select && (this.select.disabled || this.select.hasAttribute('disabled')));
      };

      this.buildMenuFromOptions();
      this.syncFromSelect();

      this.boundOutside = (e) => {
        if (!this.contains(/** @type {Node} */ (e.target))) this.close();
      };

      this.trigger.addEventListener('click', () => {
        if (this.isDisabled()) return;
        this.toggle();
      });
      this.select.addEventListener('change', () => this.syncFromSelect());
      // Rebuild menu when options are updated programmatically
      this.select.addEventListener('input-select:options-updated', () => this.rebuildOptions());
      this._observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'childList') {
            this.rebuildOptions();
            break;
          }
        }
      });
      this._observer.observe(this.select, { childList: true });
      this.addEventListener('keydown', (e) => this.onKeydown(/** @type {KeyboardEvent} */ (e)));

      // Keep trigger in sync if select.disabled is toggled dynamically.
      this._attrObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes' && m.attributeName === 'disabled') {
            this.syncDisabledState();
            break;
          }
        }
      });
      this._attrObserver.observe(this.select, { attributes: true, attributeFilter: ['disabled'] });
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
      if (this._attrObserver) this._attrObserver.disconnect();
      document.removeEventListener('click', this.boundOutside, { capture: true });
    }

    syncDisabledState() {
      if (!this.select || !this.trigger) return;
      const disabled = this.isDisabled();
      this.trigger.disabled = disabled;
      this.trigger.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      if (disabled) this.close();
    }

    buildMenuFromOptions() {
      if (!this.select || !this.menu) return;
      this.menu.innerHTML = '';
      const frag = document.createDocumentFragment();
      Array.from(this.select.options).forEach((opt, idx) => {
        const div = document.createElement('div');
        div.className = 'is-option';
        div.setAttribute('role', 'option');
        div.dataset.value = opt.value;
        div.textContent = opt.textContent;
        if (opt.disabled) div.setAttribute('aria-disabled', 'true');
        if (opt.selected) div.setAttribute('aria-selected', 'true');
        div.addEventListener('click', () => {
          if (this.isDisabled()) return;
          if (opt.disabled) return;
          this.setValue(opt.value, true);
          this.close();
        });
        frag.appendChild(div);
      });
      this.menu.appendChild(frag);
    }

    rebuildOptions() {
      // Rebuild menu and resync selected value/display
      this.buildMenuFromOptions();
      this.syncFromSelect();
    }

    /** @param {string} val @param {boolean} notify */
    setValue(val, notify) {
      if (!this.select) return;
      const prev = this.select.value;
      this.select.value = val;
      this.highlightSelected();
      this.syncTriggerText();
      if (notify && prev !== val) this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncFromSelect() {
      this.syncDisabledState();
      this.highlightSelected();
      this.syncTriggerText();
    }

    syncTriggerText() {
      if (!this.select) return;
      const selected = this.select.options[this.select.selectedIndex];
      const span = this.querySelector('.is-value');
      if (span) span.textContent = selected ? selected.textContent : '';
    }

    highlightSelected() {
      if (!this.select || !this.menu) return;
      const val = this.select.value;
      this.menu.querySelectorAll('.is-option').forEach((el) => {
        const optEl = /** @type {HTMLElement} */ (el);
        optEl.setAttribute('aria-selected', optEl.dataset.value === val ? 'true' : 'false');
      });
    }

    toggle() {
      if (this.isDisabled()) return;
      (this.isOpen() ? this.close() : this.open());
    }
    isOpen() { return !!(this.menu && !this.menu.classList.contains('hidden')); }
    open() {
      if (this.isDisabled() || !this.menu || !this.trigger) return;
      this.menu.classList.remove('hidden');
      this.trigger.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.boundOutside, { capture: true });
    }
    close() {
      if (!this.menu || !this.trigger) return;
      this.menu.classList.add('hidden');
      this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.boundOutside, { capture: true });
    }

    /** @param {KeyboardEvent} e */
    onKeydown(e) {
      if (this.isDisabled()) return;
      if (!this.isOpen() && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        this.open();
        return;
      }
      if (this.isOpen()) {
        if (e.key === 'Escape') {
          this.close();
          return;
        }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          this.moveSelection(e.key === 'ArrowDown' ? 1 : -1);
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          const current = this.menu ? this.menu.querySelector('.is-option[aria-selected="true"]') : null;
          if (current) {
            const val = /** @type {HTMLElement} */ (current).dataset.value;
            if (typeof val === 'string') {
              this.setValue(val, true);
              this.close();
            }
          }
        }
      }
    }

    /** @param {number} delta */
    moveSelection(delta) {
      if (!this.select) return;
      const values = Array.from(this.select.options).filter(o => !o.disabled).map(o => o.value);
      if (!values.length) return;
      const idx = values.indexOf(this.select.value);
      const next = Math.max(0, Math.min(values.length - 1, idx + delta));
      const nextValue = values[next];
      if (typeof nextValue === 'string') this.setValue(nextValue, true);
    }
  }

  if (!customElements.get('input-select')) {
    customElements.define('input-select', InputSelect);
  }
})();
